import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Alert,
  Snackbar,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  Chip,
  Fade,
  FormLabel
} from '@mui/material';
import { Send, CheckCircle, ArrowBack, ArrowForward } from '@mui/icons-material';
import * as Icons from '@mui/icons-material';
import { getActiveFormConfig, getInitialFormData } from '../config/applicationFormConfig';
import FormField from '../components/FormField';

const Application = () => {
  // Read the config once at mount — picks up any admin-saved changes from localStorage
  const [activeConfig] = useState(getActiveFormConfig);
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState(() => getInitialFormData(activeConfig));
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const steps = activeConfig.steps;
  const currentStep = steps[activeStep];
  const isLastStep = activeStep === steps.length - 1;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleButtonClick = (fieldName) => {
    if (fieldName === 'addAnotherParent') {
      setFormData(prev => ({ ...prev, parentCount: Math.min((prev.parentCount || 1) + 1, 3) }));
    } else if (fieldName === 'removeParent2') {
      setFormData(prev => ({ ...prev, parentCount: 1 }));
    } else if (fieldName === 'removeParent3') {
      setFormData(prev => ({ ...prev, parentCount: 2 }));
    } else if (fieldName === 'addSibling') {
      setFormData(prev => ({ ...prev, siblingCount: Math.min((prev.siblingCount || 0) + 1, 5) }));
    } else if (fieldName === 'removeSibling1') {
      setFormData(prev => ({ ...prev, siblingCount: 0 }));
    } else if (fieldName === 'removeSibling2') {
      setFormData(prev => ({ ...prev, siblingCount: 1 }));
    } else if (fieldName === 'removeSibling3') {
      setFormData(prev => ({ ...prev, siblingCount: 2 }));
    } else if (fieldName === 'removeSibling4') {
      setFormData(prev => ({ ...prev, siblingCount: 3 }));
    } else if (fieldName === 'removeSibling5') {
      setFormData(prev => ({ ...prev, siblingCount: 4 }));
    }
  };

  const isFieldVisible = (field) => {
    if (field.visible === false) return false;
    if (typeof field.visible === 'function') return field.visible(formData);
    if (field.conditional) {
      return formData[field.conditional.field] === field.conditional.value;
    }
    return true;
  };

  // Returns top-level visible fields for a step
  const getVisibleFields = (step) => step.fields.filter(isFieldVisible);

  // Returns all fields (including box children) for validation purposes
  const getAllValidatableFields = (step) => {
    const all = [];
    step.fields.forEach(field => {
      if (isFieldVisible(field)) {
        if (field.type === 'box' && field.children) {
          field.children.forEach(child => {
            if (isFieldVisible(child)) all.push(child);
          });
        } else {
          all.push(field);
        }
      }
    });
    return all;
  };

  const validateStep = (stepIndex) => {
    const newErrors = {};
    getAllValidatableFields(steps[stepIndex]).forEach(field => {
      if (!field.required) return;
      const val = formData[field.name];
      const empty = field.type === 'checkbox' ? !val : !val?.toString().trim();
      if (empty) {
        newErrors[field.name] = field.validation?.required || `${field.label} is required`;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getCompletionPercentage = () => {
    const allRequired = steps.flatMap(s =>
      s.fields.flatMap(f => f.type === 'box' && f.children ? f.children : f)
    ).filter(f => f.required);
    if (!allRequired.length) return 0;
    const filled = allRequired.filter(f => {
      const val = formData[f.name];
      return f.type === 'checkbox' ? val === true : val?.toString().trim();
    });
    return Math.round((filled.length / allRequired.length) * 100);
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => setActiveStep(prev => prev - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(activeStep)) return;
    setIsSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/applications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Submission failed');
      }
      setShowSuccess(true);
      setFormData(getInitialFormData());
      setActiveStep(0);
    } catch (err) {
      setErrors({ _submit: err.message || 'Failed to submit application. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fields with xs:12 (and no smaller sm) are always full-width.
  // All other fields grow/shrink via flex to fill the row — "stretch where possible".
  // Fields with gridSize.maxWidth are capped (e.g. State, ZIP) and won't grow past it.
  const getWrapperSx = (field) => {
    const sm = field.gridSize?.sm;
    const xs = field.gridSize?.xs ?? 12;
    const maxWidth = field.gridSize?.maxWidth;
    if (xs === 12 && (!sm || sm === 12)) {
      return { flex: '0 0 100%', minWidth: 0 };
    }
    const minWidth = sm <= 4 ? '180px' : sm <= 6 ? '240px' : '320px';
    if (maxWidth) {
      // Don't grow — stay compact (State, ZIP, etc.)
      return { flex: `0 1 ${minWidth}`, minWidth: 0, maxWidth };
    }
    return { flex: `1 1 ${minWidth}`, minWidth, maxWidth: '100%' };
  };

  const renderField = (field) => {
    if (field.type === 'box') {
      if (!isFieldVisible(field)) return null;
      return (
        <Box key={field.name} sx={{ flex: '0 0 100%', minWidth: 0 }}>
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
            {field.label && (
              <FormLabel sx={{ fontWeight: 600, fontSize: '1rem', display: 'block', mb: 2 }}>
                {field.label}
              </FormLabel>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {(field.children || []).filter(isFieldVisible).map(child => (
                <Box key={child.name} sx={getWrapperSx(child)}>
                  <FormField
                    field={child}
                    value={formData[child.name] ?? ''}
                    onChange={handleChange}
                    onButtonClick={handleButtonClick}
                    error={errors[child.name]}
                  />
                </Box>
              ))}
            </Box>
          </Paper>
        </Box>
      );
    }

    return (
      <Box key={field.name} sx={getWrapperSx(field)}>
        <FormField
          field={field}
          value={formData[field.name] ?? ''}
          onChange={handleChange}
          onButtonClick={handleButtonClick}
          error={errors[field.name]}
        />
      </Box>
    );
  };

  const StepIcon = ({ step, index }) => {
    const Icon = Icons[step.icon] || Icons.Assignment;
    return (
      <Box sx={{
        width: 40, height: 40, borderRadius: '50%',
        bgcolor: index <= activeStep ? 'primary.main' : 'grey.300',
        color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all 0.3s ease'
      }}>
        <Icon sx={{ fontSize: 20 }} />
      </Box>
    );
  };

  return (
    <Box>
      {/* Hero */}
      <Box sx={{
        background: 'linear-gradient(135deg, #050979 0%, #1976d2 100%)',
        color: 'white', py: { xs: 6, md: 8 }, textAlign: 'center'
      }}>
        <Container maxWidth="lg">
          <Typography variant="h2" sx={{ fontWeight: 700, mb: 2, fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
            Student Application
          </Typography>
          <Typography variant="h5" sx={{ opacity: 0.9, fontWeight: 300, maxWidth: 600, mx: 'auto' }}>
            Join the Eagles Refuge Academy family
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 6 }}>
        {/* Progress */}
        <Paper elevation={2} sx={{ mb: 4, p: 3, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Application Progress</Typography>
            <Chip label={`${getCompletionPercentage()}% Complete`} color="primary" variant="outlined" />
          </Box>
          <LinearProgress variant="determinate" value={getCompletionPercentage()} sx={{ height: 8, borderRadius: 4 }} />
        </Paper>

        {/* Stepper */}
        <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 3, overflowX: 'auto' }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((step, index) => (
              <Step key={step.id}>
                <StepLabel StepIconComponent={() => <StepIcon step={step} index={index} />}>
                  <Typography variant="caption" sx={{ fontWeight: index === activeStep ? 700 : 400 }}>
                    {step.label}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* Form */}
        <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Fade in key={activeStep} timeout={400}>
              <Box sx={{ minHeight: 400, mb: 4 }}>
                {/* Step header */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                  {(() => {
                    const Icon = Icons[currentStep.icon] || Icons.Assignment;
                    return <Icon sx={{ color: 'primary.main', mr: 2, fontSize: 32 }} />;
                  })()}
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>{currentStep.label}</Typography>
                    {currentStep.description && (
                      <Typography variant="body2" color="text.secondary">{currentStep.description}</Typography>
                    )}
                  </Box>
                </Box>

                {/* Fields */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5 }}>
                  {getVisibleFields(currentStep).map(renderField)}
                </Box>
              </Box>
            </Fade>

            {/* Submit error */}
            {errors._submit && (
              <Alert severity="error" sx={{ mt: 2 }}>{errors._submit}</Alert>
            )}

            {/* Navigation */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                type="button"
                onClick={handleBack}
                disabled={activeStep === 0}
                variant="outlined"
                size="large"
                startIcon={<ArrowBack />}
                sx={{ px: 4, py: 1.5, fontWeight: 600, borderRadius: 2 }}
              >
                Back
              </Button>

              {isLastStep ? (
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isSubmitting}
                  endIcon={<Send />}
                  sx={{ px: 4, py: 1.5, fontWeight: 600, borderRadius: 2 }}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Application'}
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={handleNext}
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForward />}
                  sx={{ px: 4, py: 1.5, fontWeight: 600, borderRadius: 2 }}
                >
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </Paper>

        {/* What happens next */}
        <Paper elevation={1} sx={{ p: 3, mt: 4, borderRadius: 2, bgcolor: 'grey.50' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
            What happens next?
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>• We will review your application within 2-3 business days</Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>• You will receive a confirmation email with next steps</Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>• We may schedule a meeting to discuss your child's needs</Typography>
          <Typography variant="body1">• Our admissions team will contact you with the final decision</Typography>
        </Paper>
      </Container>

      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowSuccess(false)} severity="success" sx={{ width: '100%' }} icon={<CheckCircle />}>
          Application submitted successfully! We will contact you soon.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Application;
